import type { ImageSpec } from "./imageStandards";

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read image file"));
    };
    img.src = url;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Failed to process image"))),
      type,
      quality,
    );
  });
}

function drawCover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  targetW: number,
  targetH: number,
) {
  const scale = Math.max(targetW / img.width, targetH / img.height);
  const sw = targetW / scale;
  const sh = targetH / scale;
  const sx = (img.width - sw) / 2;
  const sy = (img.height - sh) / 2;
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, targetW, targetH);
}

function drawContain(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  targetW: number,
  targetH: number,
) {
  const scale = Math.min(targetW / img.width, targetH / img.height);
  const dw = img.width * scale;
  const dh = img.height * scale;
  const dx = (targetW - dw) / 2;
  const dy = (targetH - dh) / 2;
  ctx.drawImage(img, dx, dy, dw, dh);
}

/** Normalize uploads to platform standard dimensions (JPEG). */
export async function processImageForUpload(file: File, spec: ImageSpec): Promise<File> {
  const img = await loadImage(file);

  if (img.width < spec.minWidth || img.height < spec.minHeight) {
    throw new Error(
      `Image is too small. Minimum ${spec.minWidth}×${spec.minHeight}px, yours is ${img.width}×${img.height}px.`,
    );
  }

  const canvas = document.createElement("canvas");
  canvas.width = spec.width;
  canvas.height = spec.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not process image");

  ctx.fillStyle = spec.background;
  ctx.fillRect(0, 0, spec.width, spec.height);

  if (spec.fit === "cover") {
    drawCover(ctx, img, spec.width, spec.height);
  } else {
    drawContain(ctx, img, spec.width, spec.height);
  }

  const blob = await canvasToBlob(canvas, "image/jpeg", 0.92);
  const baseName = file.name.replace(/\.[^.]+$/, "") || "image";
  return new File([blob], `${baseName}-${spec.width}x${spec.height}.jpg`, { type: "image/jpeg" });
}
